import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { getRollNumbers, type RollNumberRow } from "../../../service/roll-number"
import { getResultBreakdownById } from "../../../service/result-breakdown"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"

// Backend populates termId/sessionId/branchId in place (same field name
// becomes the populated object) rather than adding separate term/session/
// branch fields -- matches the pattern already used in result-breakdown/index.tsx.
const titleOf = (field: unknown): string => (field && typeof field === "object" ? (field as any).title : "")
const nameOf = (field: unknown): string => (field && typeof field === "object" ? (field as any).name : "")

const RollNumberSheet = () => {
  const [params] = useSearchParams()
  const currentBranch = useSelector(selectCurrentBranch)
  const [rows, setRows] = useState<RollNumberRow[]>([])
  const [breakdown, setBreakdown] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  const breakdownId = params.get("breakdown") || ""
  const classId = params.get("class") || ""
  const sectionId = params.get("section") || ""
  const autoprint = params.get("autoprint") === "1"

  useEffect(() => {
    if (!breakdownId) return
    setLoading(true)
    Promise.all([
      getRollNumbers({ breakdownId, classId: classId || undefined, sectionId: sectionId || undefined }),
      getResultBreakdownById(breakdownId),
    ])
      .then(([r, b]) => { setRows(r); setBreakdown(b) })
      .catch((e: any) => toast.error(e?.response?.data?.message || "Failed to load roll numbers"))
      .finally(() => setLoading(false))
  }, [breakdownId, classId, sectionId])

  useEffect(() => {
    if (autoprint && !loading && rows.length > 0) {
      const t = setTimeout(() => window.print(), 300)
      return () => clearTimeout(t)
    }
  }, [autoprint, loading, rows.length])

  // One page per class+section group, matching how RMS prints roll number
  // rosters -- a page break between groups so the browser's print/PDF
  // output naturally paginates one section per page.
  const groups = useMemo(() => {
    const byClass = new Map<string, { className: string; bySection: Map<string, { sectionName: string; rows: RollNumberRow[] }> }>()
    for (const r of rows) {
      if (!byClass.has(r.class._id)) byClass.set(r.class._id, { className: r.class.name, bySection: new Map() })
      const cls = byClass.get(r.class._id)!
      if (!cls.bySection.has(r.section._id)) cls.bySection.set(r.section._id, { sectionName: r.section.name, rows: [] })
      cls.bySection.get(r.section._id)!.rows.push(r)
    }
    return Array.from(byClass.values()).flatMap(cls =>
      Array.from(cls.bySection.values()).map(sec => ({
        className: cls.className,
        sectionName: sec.sectionName,
        rows: sec.rows.slice().sort((a, b) => a.rollNumber.localeCompare(b.rollNumber, undefined, { numeric: true })),
      }))
    )
  }, [rows])

  if (!breakdownId) {
    return <div className="p-4 text-muted">Missing breakdown in the URL.</div>
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <style>{`
          @media print {
            @page { size: A4 portrait; margin: 10mm; }
            .no-print { display: none !important; }
            .rn-group { page-break-after: always; }
            .rn-group:last-child { page-break-after: auto; }
          }

          /* Modelled on RMS's own roll-number print template (same roster,
             same job) -- a plain blue-header table read as noticeably
             barer than it, so this borrows its structure (stat-style
             student count, zebra rows, tabular-nums, a footer) while
             keeping our own blue header so it still matches every other
             print page in this app. */
          .rn-titlerow {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            margin: 2mm 0 3mm;
            padding-bottom: 2mm;
            border-bottom: 1px solid #333;
          }
          .rn-titlerow .rn-title { font-size: 13px; font-weight: 700; }
          .rn-titlerow .rn-title .sep { color: #94a3b8; margin: 0 5px; font-weight: 400; }
          .rn-titlerow .rn-stat { text-align: right; font-size: 10px; color: #475569; line-height: 1.25; }
          .rn-titlerow .rn-stat strong { display: block; font-size: 15px; color: #111; }

          .rn-table { table-layout: fixed; }
          .rn-table th {
            text-transform: uppercase;
            letter-spacing: 0.04em;
            font-size: 9px !important;
          }
          .rn-table td.rn-num { font-variant-numeric: tabular-nums; text-align: center; }
          .rn-table tbody tr:nth-child(even) td { background: #f3f6fa; }

          .rn-footer {
            display: flex;
            justify-content: space-between;
            margin-top: 2mm;
            padding-top: 1.5mm;
            border-top: 1px dotted #cbd5e1;
            font-size: 8px;
            color: #94a3b8;
          }
          @media screen {
            .rn-footer { display: none; }
          }
        `}</style>

        <div className="d-flex align-items-center justify-content-between mb-3 no-print">
          <span className="text-muted">{rows.length} roll number(s) across {groups.length} section group(s)</span>
          <button className="btn btn-dark btn-sm" onClick={() => window.print()} disabled={loading || rows.length === 0}>
            Print
          </button>
        </div>

        {loading ? (
          <div className="text-center text-muted py-5">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-muted py-5">No roll numbers generated yet for this selection.</div>
        ) : (
          // The site-wide print stylesheet hides everything except
          // `.printable-content` (`body * { visibility: hidden }`) -- every
          // other print page (Marks Tabulation Sheet, Report Card Sheet)
          // wraps its sheet in this class for exactly that reason. This page
          // never did, so printing produced a blank page even though the
          // on-screen table was fully loaded.
          <div className="printable-content">
            {groups.map((g, groupIdx) => (
              <div className="rn-group mb-4" key={`${g.className}-${g.sectionName}`}>
                {/* Shown only when printing (site-wide .print-only-header rule) --
                    same black-logo branding block every other print page in the
                    app uses (Student List, Admission List, etc.), so this sheet
                    matches them instead of printing as a bare table. */}
                <div className="print-only-header">
                  <div className="school-brand">
                    <img
                      src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"}
                      alt={campusBrandName(currentBranch)}
                    />
                    <strong>
                      {campusBrandName(currentBranch)}
                      {campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}
                    </strong>
                  </div>
                  <div className="report-title">
                    Roll Number Sheet
                    <div className="small" style={{ fontWeight: 400 }}>
                      {titleOf(breakdown?.termId) || "Term"} · {titleOf(breakdown?.sessionId)}
                    </div>
                  </div>
                  <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
                </div>

                {/* On-screen preview only -- the print-only-header above covers
                    campus/term/session on paper, so this doesn't need to repeat. */}
                <div className="d-flex align-items-center justify-content-between mb-2 no-print">
                  <div>
                    <h5 className="mb-0 fw-bold">{nameOf(breakdown?.branchId) || "Campus"}</h5>
                  </div>
                  <div className="text-end">
                    <span className="badge bg-dark">{titleOf(breakdown?.termId) || "Term"}</span>
                    <div className="small text-muted">{titleOf(breakdown?.sessionId)}</div>
                  </div>
                </div>

                <div className="rn-titlerow">
                  <div className="rn-title">
                    {g.className}
                    <span className="sep">·</span>
                    Section {g.sectionName}
                  </div>
                  <div className="rn-stat">
                    <strong>{g.rows.length}</strong>
                    student{g.rows.length === 1 ? "" : "s"}
                  </div>
                </div>

                <table className="table table-bordered table-sm rn-table">
                  <colgroup>
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "32%" }} />
                    <col style={{ width: "33%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>SR #</th>
                      <th>ROLL #</th>
                      <th>GR #</th>
                      <th>STUDENT NAME</th>
                      <th>FATHER'S NAME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r, idx) => (
                      <tr key={r._id}>
                        <td className="rn-num text-muted">{idx + 1}</td>
                        <td className="rn-num fw-semibold">{r.rollNumber}</td>
                        <td className="rn-num">{r.grNumber || "—"}</td>
                        <td>{r.studentName}</td>
                        <td>{r.fatherName || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="rn-footer">
                  <span>Generated {new Date().toLocaleDateString("en-GB")}</span>
                  <span>{groupIdx + 1} / {groups.length} sections</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default RollNumberSheet
